import React, { useState, useContext, useRef } from 'react'
import { createPortal } from 'react-dom';
import { LassieContext } from "../../context/LassieProvider";
import { organizationAPI } from '../../api';
import CenteredPopup from '../centered-popup/CenteredPopup';
import WplInput from '../wpl-input/WplInput';
import WplButton from '../wpl-button/WplButton';
import OptionsMenu from '../options-menu/OptionsMenu';
import optionIcon from "../../assets/img/option-icon.png";
import addIconBlack from '../../assets/img/add-icon-black.png'
import editIconGray from '../../assets/img/edit-gray.png';
import deleteIconGray from '../../assets/img/delete-gray.png';

export default function InspectionTemplatesManager() {
    const { organization,
        setOrganizationLoading, inspectionTemplates, setInspectionTemplates, setOrganizationPopup, setSelectedComment,
        setUpdateComment, createInspectionTemplate, formatDueInHours, newDueDate, setNewDueDate, editMode, setEditMode } = useContext(LassieContext);
    const [showNewInspectionPopup, setShowNewInspectionPopup] = useState(false);
    const [showOptionsId, setShowOptionsId] = useState({ id: null, is_used: false });
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [newInspection, setNewInspection] = useState({
        title: '',
        comment: '',
        overdue_remind_in_days: null,
        inspection_type: '',
        inspection_method: ''
    });
    const iconRefs = useRef({});

    const deleteInspectionTemplate = async (template_id, is_used) => {
        try {
            const response = await organizationAPI.deleteInspectionTemplate(template_id);
            if (response === 'OK') {
                setInspectionTemplates(prev => prev.filter(template => template.id !== template_id));
                setSelectedComment(null);
                closePopupAndReset()
            }
        } catch (error) {
            console.error(`Error deleting comment template:`, error);
            const rawErrorData = error.response?.data;
            const errorText = typeof rawErrorData === 'string' ? rawErrorData.trim() : null;

            const displayMessage = errorText || error.message

            setOrganizationPopup({
                show: true,
                status: 'warning',
                title: 'Something went wrong',
                buttonText: 'Close',
                buttonStatus: 'warning',
                text: displayMessage,
            });
        }
    };

    const updateInspectionTemplate = async (template_id, title, comment, due_in_hours, overdue_remind_in_days, inspection_type, inspection_method) => {
        setOrganizationLoading(prev => ({ ...prev, templates: true }));
        try {
            const updatedTemplate = await organizationAPI.updateInspectionTemplate(template_id, title, comment, { due_in_hours, overdue_remind_in_days, overdue_remind_in_days, inspection_type, inspection_method });
            if (updatedTemplate) {
                setInspectionTemplates(prev =>
                    prev.map(comment =>
                        comment.id === updatedTemplate.id ? updatedTemplate : comment
                    )
                );
                setEditMode({ id: null, status: false });
                setSelectedComment(updatedTemplate)
            }
        } catch (error) {
            console.log(`Error updating comment template: ${error}`);
            setOrganizationPopup({
                show: true,
                status: 'warning',
                title: 'Something went wrong',
                buttonText: 'Close',
                buttonStatus: 'warning',
                text: error.response?.data
            });
        } finally {
            setOrganizationLoading(prev => ({ ...prev, templates: false }));
        }
    }

    const closePopupAndReset = () => {
        setOrganizationPopup((prev) => ({ ...prev, show: false }))
    }

    const sanitizeEmpty = (value) => value === '' || value === 'N/A' ? null : value;

    const handleNewInpectionTemplate = () => {
        const dueInHours = Number(convertToHours(newDueDate));
        const overdueInDays = Number(newInspection.overdue_remind_in_days);

        createInspectionTemplate(
            organization.id,
            newInspection.title,
            newInspection.comment,
            isNaN(dueInHours) || dueInHours === 0 ? null : dueInHours,
            isNaN(overdueInDays) || overdueInDays === 0 ? null : overdueInDays,
            sanitizeEmpty(newInspection.inspection_type),
            sanitizeEmpty(newInspection.inspection_method)
        );
        setShowNewInspectionPopup(false);
        resetForms()
    };

    const resetForms = () => {
        setNewInspection({
            title: '',
            comment: '',
            overdue_remind_in_days: null,
            inspection_type: '',
            inspection_method: ''
        });
        setNewDueDate({ unit: 'Days', value: 0 });
    }


    const convertToHours = (newDueDate) => {
        if (newDueDate.unit === 'Days') {
            return newDueDate.value * 24;
        }
        else if (newDueDate.value === 0) {
            return null;
        } else {
            return newDueDate.value;
        }
    };

    const convertFromDueInHours = (dueInHours) => {
        if (dueInHours === null || dueInHours === 0) {
            return { unit: 'Days', value: 0 };
        }
        if (dueInHours % 24 === 0) {
            return { unit: 'Days', value: dueInHours / 24 };
        } else {
            return { unit: 'Hours', value: dueInHours };
        }
    };

    return (
        <div className='templates-manager-container'>
            <div className="inspection-template-header">
                <h4>Manage inspection templates</h4>
                <img
                    src={addIconBlack}
                    alt="Create a new inspection template"
                    className="add-icon"
                    onClick={() => {
                        setEditMode({ id: null, status: false });
                        setShowNewInspectionPopup(!showNewInspectionPopup);
                        resetForms()
                    }}
                />
            </div>

            <div className='cards-area'>
                <div className='organization-table-container'>
                    <table className='organization-table'>
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Comment</th>
                                <th>Inspect within</th>
                                <th>Overdue reminder</th>
                                <th>Inspection type</th>
                                <th>Inspection method</th>
                                {editMode.status ? <th>
                                    <div className='comment-action-container'>
                                        <button className='org-btn cancel-btn'
                                            onClick={() => setEditMode({ id: null, status: false })}
                                        >X
                                        </button>
                                        <button className='org-btn success-btn'
                                            disabled={!newInspection.title || !newInspection.comment || !Number.isFinite(newInspection.overdue_remind_in_days || newInspection.overdue_remind_in_days === 0)
                                            }
                                            onClick={() => updateInspectionTemplate(editMode.id,
                                                newInspection.title,
                                                newInspection.comment,
                                                convertToHours(newDueDate),
                                                newInspection.overdue_remind_in_days,
                                                sanitizeEmpty(newInspection.inspection_type),
                                                sanitizeEmpty(newInspection.inspection_method))}
                                        >✔
                                        </button>
                                    </div></th> : <th></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {inspectionTemplates.length ? (
                                inspectionTemplates
                                    .sort((a, b) => a.title.localeCompare(b.title))
                                    .map(template => (
                                        <tr key={template.id}>
                                            <td onClick={() => {
                                                setSelectedComment(template);
                                                setUpdateComment({ status: false, is_new_comment: false });
                                            }}>
                                                {editMode.status && editMode.id === template.id ?
                                                    <input value={editMode.id === template.id ? newInspection.title : template.title}
                                                        onChange={(e) => setNewInspection(prev => ({ ...prev, title: e.target.value }))}
                                                    />
                                                    : template.title}
                                            </td>
                                            <td className='template-comment-cell'>
                                                {editMode.status && editMode.id === template.id ?
                                                    <input value={editMode.id === template.id ? newInspection.comment : template.comment}
                                                        onChange={(e) => setNewInspection(prev => ({ ...prev, comment: e.target.value }))}
                                                    />
                                                    : template.comment}
                                            </td>
                                            <td>
                                                {editMode.status && editMode.id === template.id ?
                                                    <button onClick={() => {
                                                        setOrganizationPopup({
                                                            show: true,
                                                            status: 'success',
                                                            title: 'Update due date',
                                                            buttonText: 'Cancel',
                                                            buttonStatus: 'wrong',
                                                            action: () => closePopupAndReset(),
                                                            text: `Are you sure you want to add this due date?`,
                                                            buttonText2: 'Save',
                                                            buttonStatus2: 'success',
                                                            action2: 'update-due-date',
                                                            is_due_date: true,
                                                        });
                                                    }} className='wpl-button'>{formatDueInHours(template.due_in_hours)}</button>
                                                    : formatDueInHours(template.due_in_hours)}
                                            </td>
                                            <td>
                                                {editMode.status && editMode.id === template.id ? (
                                                    <input
                                                        type='number'
                                                        min={0}
                                                        step={1}
                                                        value={
                                                            newInspection.overdue_remind_in_days === ''
                                                                ? ''
                                                                : newInspection.overdue_remind_in_days
                                                        }
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setNewInspection((prev) => ({
                                                                ...prev,
                                                                overdue_remind_in_days: value === '' ? '' : Number(value)
                                                            }));
                                                        }}
                                                    />


                                                ) : template.overdue_remind_in_days != null ? (
                                                    `${template.overdue_remind_in_days} days`
                                                ) : (
                                                    'N/A'
                                                )}
                                            </td>
                                            <td>
                                                {editMode.status && editMode.id === template.id ?
                                                    <select
                                                        className="due-date-input-cell"
                                                        value={editMode.id === template.id ? newInspection.inspection_type : template.inspection_type}
                                                        onChange={(e) =>
                                                            setNewInspection((prev) => ({
                                                                ...prev,
                                                                inspection_type: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="internal">Internal</option>
                                                        <option value="external">External</option>
                                                        <option value="N/A">N/A</option>
                                                    </select>
                                                    : template.inspection_type ? template.inspection_type : 'N/A'}
                                            </td>
                                            <td>
                                                {editMode.status && editMode.id === template.id ?
                                                    <select
                                                        className="due-date-input-cell"
                                                        value={editMode.id === template.id ? newInspection.inspection_method : template.inspection_method}
                                                        onChange={(e) =>
                                                            setNewInspection((prev) => ({
                                                                ...prev,
                                                                inspection_method: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="ground">Ground</option>
                                                        <option value="drone">Drone</option>
                                                        <option value="N/A">N/A</option>
                                                    </select>
                                                    : template.inspection_method ? template.inspection_method : 'N/A'}
                                            </td>


                                            <td
                                                className='option-cell'
                                                onClick={() => {
                                                    const icon = iconRefs.current[template.id];
                                                    if (icon) {
                                                        const rect = icon.getBoundingClientRect();
                                                        setMenuPosition({
                                                            top: rect.bottom + window.scrollY,
                                                            left: rect.left + window.scrollX + 15,
                                                        });
                                                        setShowOptionsId({ id: showOptionsId.id === template.id ? null : template.id, is_used: template.is_used });
                                                        setNewInspection({ title: template.title, comment: template.comment, overdue_remind_in_days: template.overdue_remind_in_days, inspection_type: template.inspection_type, inspection_method: template.inspection_method })
                                                        convertFromDueInHours(template.due_in_hours)
                                                    }
                                                }}>
                                                <img
                                                    src={optionIcon}
                                                    className='action-icon option-btn'
                                                    ref={(el) => (iconRefs.current[template.id] = el)}
                                                />
                                            </td>
                                        </tr>
                                    ))
                            ) : (
                                <tr>
                                    <td colSpan={2} className="no-templates-message">
                                        <p>No inspection templates saved yet.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {showOptionsId.id && createPortal(
                    <div
                        className="options-container"
                        style={{
                            position: 'absolute',
                            top: menuPosition.top,
                            left: menuPosition.left,
                            zIndex: 9999,
                        }}
                    >
                        <OptionsMenu
                            options={[
                                {
                                    label: 'Edit',
                                    onClick: () => {
                                        const template = inspectionTemplates.find(t => t.id === showOptionsId.id);
                                        if (template) {
                                            setNewInspection({
                                                title: template.title,
                                                comment: template.comment,
                                                overdue_remind_in_days: template.overdue_remind_in_days,
                                                inspection_type: template.inspection_type,
                                                inspection_method: template.inspection_method
                                            });
                                            setNewDueDate(convertFromDueInHours(template.due_in_hours));
                                            setEditMode({ id: showOptionsId.id, status: true });
                                        }
                                    },
                                    img: editIconGray
                                },
                                {
                                    label: 'Delete',
                                    onClick: () => {
                                        if (showOptionsId.is_used) {
                                            setOrganizationPopup({
                                                show: true,
                                                status: 'warning',
                                                title: 'Something went wrong',
                                                buttonText: 'Close',
                                                buttonStatus: 'warning',
                                                text: 'This template has been used in at least one inspection and cannot be deleted.',
                                            });
                                        } else {
                                            setOrganizationPopup({
                                                show: true,
                                                status: 'warning',
                                                title: 'Delete inspection template',
                                                buttonText: 'Cancel',
                                                buttonStatus: 'success',
                                                action: () => closePopupAndReset(),
                                                text: `Are you sure you want to delete this inspection template?`,
                                                buttonText2: 'Delete',
                                                buttonStatus2: 'wrong',
                                                action2: () => deleteInspectionTemplate(showOptionsId.id),
                                            }
                                            )

                                        }
                                    },
                                    img: deleteIconGray
                                },
                            ]}
                            onClose={() => setShowOptionsId({})}
                        />
                    </div>,
                    document.body
                )}

                <CenteredPopup showPopup={showNewInspectionPopup} closePopup={() => setShowNewInspectionPopup(false)} className='add-inspection-popup'>
                    <div className='centered-content'>
                        <h4>Add a new inspection template to {organization.name}</h4>
                    </div>
                    <div className='organization-inspection-container'>
                        <label className='due-date-label'>Title</label>
                        <WplInput value={newInspection.title} onChanged={(value) => setNewInspection({ ...newInspection, title: value })} />
                        <label className='due-date-label'>Comment</label>
                        <input
                            className='new-comment'
                            value={newInspection.comment}
                            onChange={(e) => setNewInspection(prev => ({ ...prev, comment: e.target.value }))}
                        />
                        <div className='due-date-selector'>
                            <div>
                                <label className='due-date-label'>Due in</label>
                                <WplInput
                                    type="number"
                                    className="due-date-input"
                                    min={0}
                                    max={100}
                                    value={newDueDate.value}
                                    onChanged={(value) => setNewDueDate({ ...newDueDate, value: value })}
                                />

                            </div>
                            <div>
                                <div className='due-date-label'>Time unit</div>
                                <WplInput
                                    options={['Days', "Hours"]}
                                    className='due-date-input'
                                    value={newDueDate.unit}
                                    onChanged={(value) => setNewDueDate({ ...newDueDate, unit: value })}

                                />
                            </div>
                        </div>
                        <div className='due-date-selector'>
                            <div>
                                <label className='due-date-label'>Overdue reminder</label>
                                <WplInput
                                    type="number"
                                    className="due-date-input"
                                    min={0}
                                    max={100}
                                    value={newInspection.overdue_remind_in_days}
                                    onChanged={(value) => setNewInspection({ ...newInspection, overdue_remind_in_days: value })}
                                />
                            </div>
                            <div>

                                <div className='due-date-label'>Time unit</div>
                                <WplInput
                                    className='due-date-input'
                                    value={'Days'}
                                />
                            </div>
                        </div>
                        <WplInput
                            title="Inspection type"
                            options={['internal', "external", 'N/A']}
                            className='due-date-input'
                            value={newInspection.inspection_type}
                            onChanged={(value) => setNewInspection({ ...newInspection, inspection_type: value })}
                        />
                        <WplInput
                            title="Inspection method"
                            options={['ground', "drone", 'N/A']}
                            className='due-date-input'
                            value={newInspection.inspection_method}
                            onChanged={(value) => setNewInspection({ ...newInspection, inspection_method: value })}
                        />
                        <div className='centered-content'>
                            <WplButton value="Create inspection template"
                                className="wpl-button invitation-btn"
                                onClick={() => handleNewInpectionTemplate()}
                                disabled={!newInspection.title || !newInspection.comment}
                            />
                        </div>
                    </div>
                </CenteredPopup>
            </div>
        </div>)
}

